import React from 'react';
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import { FormField, Button, SpaceBetween } from '@cloudscape-design/components';
import TextEditor from '../textEditor/TextEditor';
import { FileData } from '../textEditor/TextEditor';
import { FileMetadata } from '../FileDisplay';
import { MediaData } from 'src/types/helios/Media';

interface FormTextEditorProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label?: React.ReactNode;
  placeholder?: string;
  stretch?: boolean;
  showFileUpload?: boolean;
  readOnly?: boolean;
  minHeight?: number;
  maxCharLength?: number;
  onChangeMedia?: (images: FileData[]) => void;
  onFilesChange?: (data: { files: File[]; metadata: FileMetadata[] }) => void;
  initialFiles?: File[];
  initialFileMetadata?: FileMetadata[];
  mediaResponse?: MediaData[];
  programId?: string;
  businessUnit?: string;
  createdBy?: string;
  onChange?: (value: string) => void;
  showRestoreButton?: boolean;
  onRestore?: () => void;
}

/**
 * FormTextEditor - A wrapper component that integrates TextEditor with React Hook Form
 *
 * This component reduces boilerplate by handling the Controller pattern internally,
 * automatically connecting the TextEditor to React Hook Form's validation and state management.
 *
 * @example
 * ```tsx
 * <FormTextEditor
 *   control={form.control}
 *   name="questionText"
 *   label="Question Text"
 *   showFileUpload={false}
 * />
 * ```
 */
export function FormTextEditor<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  stretch = true,
  showFileUpload = false,
  readOnly = false,
  minHeight,
  maxCharLength,
  onChangeMedia,
  onFilesChange,
  initialFiles,
  initialFileMetadata,
  mediaResponse,
  programId,
  businessUnit,
  createdBy,
  onChange: onChangeCallback,
  showRestoreButton = false,
  onRestore,
}: FormTextEditorProps<TFieldValues>) {
  const labelContent =
    showRestoreButton && onRestore ? (
      <SpaceBetween direction="horizontal" size="xs">
        <span>{label}</span>
        <span title="Restore original content">
          <Button
            variant="inline-icon"
            iconName="refresh"
            ariaLabel="Restore original content"
            onClick={onRestore}
          />
        </span>
      </SpaceBetween>
    ) : (
      label
    );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <FormField
          label={labelContent}
          stretch={stretch}
          errorText={fieldState.error?.message}
        >
          <TextEditor
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              if (onChangeCallback) {
                onChangeCallback(value);
              }
            }}
            placeholder={placeholder}
            showFileUpload={showFileUpload}
            readOnly={readOnly}
            minHeight={minHeight}
            maxCharLength={maxCharLength}
            onChangeMedia={onChangeMedia}
            onFilesChange={onFilesChange}
            initialFiles={initialFiles}
            initialFileMetadata={initialFileMetadata}
            mediaResponse={mediaResponse}
            programId={programId}
            businessUnit={businessUnit}
            createdBy={createdBy}
          />
        </FormField>
      )}
    />
  );
}
